import { useSyncExternalStore } from 'react';
import type { Vector3 } from '@kitware/vtk.js/types';

// World-space offsets of labels the user is dragging right now, from the annotation's label
// anchor to the label's top left corner. Shared outside React so the drag handlers, which live
// above the label, can move it without every mouse move going through a saved annotation update.
let overrides: ReadonlyMap<string, Vector3> = new Map();
const listeners = new Set<() => void>();
const bases = new Map<string, () => Vector3 | null>();

const key = (viewportId: string, id: string) => `${viewportId}:${id}`;

export function setLabelWorldOffset(viewportId: string, id: string, offset: Vector3 | null): void {
  const current = overrides.get(key(viewportId, id)) ?? null;
  if (current === offset) return;

  const next = new Map(overrides);
  if (offset == null) next.delete(key(viewportId, id));
  else next.set(key(viewportId, id), offset);
  overrides = next;
  listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLabelWorldOffset(viewportId: string, id: string): Vector3 | undefined {
  return useSyncExternalStore(subscribe, () => overrides.get(key(viewportId, id)));
}

/** A label tells the store how to read where it is now, so a drag can start from there. */
export function registerLabelBase(
  viewportId: string,
  id: string,
  getBase: () => Vector3 | null
): () => void {
  bases.set(key(viewportId, id), getBase);
  return () => {
    if (bases.get(key(viewportId, id)) === getBase) bases.delete(key(viewportId, id));
  };
}

export function getLabelBaseOffset(viewportId: string, id: string): Vector3 | null {
  return bases.get(key(viewportId, id))?.() ?? null;
}

const clamps = new Map<string, (offset: Vector3) => Vector3>();

/** A label tells the store how to keep an offset inside its viewport, so it can't be dragged out. */
export function registerLabelClamp(
  viewportId: string,
  id: string,
  clamp: (offset: Vector3) => Vector3
): () => void {
  clamps.set(key(viewportId, id), clamp);
  return () => {
    if (clamps.get(key(viewportId, id)) === clamp) clamps.delete(key(viewportId, id));
  };
}

export function clampLabelOffset(viewportId: string, id: string, offset: Vector3): Vector3 {
  return clamps.get(key(viewportId, id))?.(offset) ?? offset;
}
